/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useObservable } from '@squiz/dxp-console-support';
import { useMemo } from 'react';
import { Observable } from 'rxjs';
import { JsonObject } from 'type-fest';
import { EntityEventSchedule } from '../../Entities';
import { EventBusGetScheduleDto } from '../../EventBus.service.dto';
import { getEventBusService } from '../../Utilities/GetEventBusService/GetEventBusService';

/**
 * The result expected of the useEventSchedule hook.
 * @export
 */
export type EventScheduleResult = [EntityEventSchedule | null, JsonObject | null];

/**
 * React hook to get a specified event subscription rule.
 */
export function useEventSchedule(subscriptionId: string): EventScheduleResult {
  const eventBusService = getEventBusService();

  const eventSchedule$ = useMemo((): Observable<EntityEventSchedule | null> => {
    const dto: EventBusGetScheduleDto = {
      scheduleName: subscriptionId,
    };

    return eventBusService.getEventSchedule(dto);
  }, [eventBusService, subscriptionId]);

  const [eventSchedule, error] = useObservable(eventSchedule$, [eventBusService]);

  return useMemo((): EventScheduleResult => {
    return [eventSchedule as EntityEventSchedule, error as JsonObject];
  }, [error, eventSchedule]);
}
